import React, { useState, useEffect } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate } from 'react-router-dom';
import API from './api';
import ProductNav from './components/ProductNav';
import SideHelpPanel from './components/SideHelpPanel';

// ─── STATIC CONFIG ──────────────────────────────────────────────────────────

const CURRENT_GENERATORS = 16000;

const MILESTONES = [
  { generators: 16000,  label: '16k'  },
  { generators: 35000,  label: '35k'  },
  { generators: 70000,  label: '70k'  },
  { generators: 100000, label: '100k' },
  { generators: 140000, label: '140k' },
  { generators: 190000, label: '190k' },
  { generators: 250000, label: '250k' },
];

// UNUSED (nothing reads this). It is a growth ladder whose first column was the old
// base price set; those base prices are now 9.99 / 25 / 40 / 65 / 130. The ladder's
// later columns are a separate commercial construct with no current authority, so it is
// left exactly as it was rather than half-corrected. Nothing renders it.
// eslint-disable-next-line no-unused-vars
const MILESTONE_PRICES = {
  artisan:     { 16000: '$6.99',  35000: '$7.99',  70000: '$8.99',  100000: '$9.99',  140000: '$10.99', 190000: '$11.99', 250000: '$12.99' },
  architect:   { 16000: '$16.99', 35000: '$18.99', 70000: '$20.99', 100000: '$22.99', 140000: '$24.99', 190000: '$26.99', 250000: '$28.99' },
  visionary:   { 16000: '$27.99', 35000: '$30.99', 70000: '$33.99', 100000: '$36.99', 140000: '$39.99', 190000: '$42.99', 250000: '$45.99' },
  creator_pro: { 16000: '$44.99', 35000: '$49.99', 70000: '$54.99', 100000: '$59.99', 140000: '$64.99', 190000: '$69.99', 250000: '$74.99' },
  commercial:  { 16000: '$84.99', 35000: '$94.99', 70000: '$104.99', 100000: '$114.99', 140000: '$124.99', 190000: '$134.99', 250000: '$144.99' },
};

const MILESTONE_BONUS_CREDITS = {
  artisan:     { 16000: 0, 35000: 5,  70000: 5,  100000: 5,  140000: 5,  190000: 5,  250000: 5  },
  architect:   { 16000: 0, 35000: 15, 70000: 15, 100000: 15, 140000: 15, 190000: 15, 250000: 15 },
  visionary:   { 16000: 0, 35000: 30, 70000: 30, 100000: 30, 140000: 30, 190000: 30, 250000: 30 },
  creator_pro: { 16000: 0, 35000: 50, 70000: 50, 100000: 50, 140000: 50, 190000: 50, 250000: 50 },
  commercial:  { 16000: 0, 35000: 70, 70000: 70, 100000: 70, 140000: 70, 190000: 70, 250000: 70 },
};

const BASE_CREDITS = {
  artisan: 80, architect: 220, visionary: 420, creator_pro: 700, commercial: 1100,
};

// price is RENDERED on this page, so it must be the amount Stripe actually charges.
// Previously 6.99 / 16.99 / 27.99 / 44.99 / 84.99, which no Stripe Price matched.
const PLAN_CONFIG = {
  artisan:     { name: 'Artisan',     color: '#10b981', price: '$9.99' },
  architect:   { name: 'Architect',   color: '#8b5cf6', price: '$25'   },
  visionary:   { name: 'Visionary',   color: '#f97316', price: '$40'   },
  creator_pro: { name: 'Creator Pro', color: '#0ea5e9', price: '$65'   },
  commercial:  { name: 'Commercial',  color: '#F59E0B', price: '$130'  },
};

const SUBSCRIPTION_PLANS = ['artisan', 'architect', 'visionary', 'creator_pro', 'commercial'];
const TOP_PLANS    = ['artisan', 'architect', 'visionary'];
const BOTTOM_PLANS = ['creator_pro', 'commercial'];

const PLAN_URGENCY = {
  artisan:     'Entry-level tier position',
  architect:   'Best balance of cost and output',
  visionary:   'For consistent weekly use',
  creator_pro: 'For heavy creators',
  commercial:  'For teams and commercial use',
};

const PLAN_USAGE = {
  artisan:     '~20-40 images/month',
  architect:   '~60-120 images/month',
  visionary:   '~120-240 images/month',
  creator_pro: '~200-400 images/month',
  commercial:  '~400-800 images/month',
};

const PLAN_BEST_FOR = {
  artisan:     'Casual use',
  architect:   'Most users',
  visionary:   'Regular creators',
  creator_pro: 'Advanced workflows',
  commercial:  'Business use',
};

const GRANDFATHER_CAP = {
  16000:  100000,
  35000:  100000,
  70000:  100000,
  100000: 190000,
  140000: 250000,
  190000: 400000,
  250000: 400000,
};

function getEntryMilestone(currentGenerators) {
  let entry = MILESTONES[0].generators;
  for (const m of MILESTONES) {
    if (m.generators <= currentGenerators) entry = m.generators;
    else break;
  }
  return entry;
}

function getBonusCap(entryMilestone) {
  return GRANDFATHER_CAP[entryMilestone] || 100000;
}

function getNextMilestone(current) {
  for (let i = 0; i < MILESTONES.length; i++) {
    if (MILESTONES[i].generators > current) return MILESTONES[i];
  }
  return MILESTONES[MILESTONES.length - 1];
}

function getCapLabel(capGen) {
  const m = MILESTONES.find(x => x.generators === capGen);
  return m ? m.label : `${capGen / 1000}k`;
}

function getBonusCount(slug, entryGen, capGen) {
  return MILESTONES.filter(m => m.generators > entryGen && m.generators <= capGen && (MILESTONE_BONUS_CREDITS[slug][m.generators] || 0) > 0).length;
}

function getMaxCredits(slug, entryGen, capGen) {
  const base = BASE_CREDITS[slug];
  const bonus = MILESTONE_BONUS_CREDITS[slug];
  let total = base;
  for (const m of MILESTONES) {
    if (m.generators > entryGen && m.generators <= capGen) {
      total += (bonus[m.generators] || 0);
    }
  }
  return total;
}

// ─── PLAN CARD SUBCOMPONENT ──────────────────────────────────────────────────

function PlanCard({ slug, userPlanSlug, entryMilestone, bonusCap, capLabel, innerPad }) {
  const cfg        = PLAN_CONFIG[slug];
  const isUser     = userPlanSlug === slug;
  const isPopular  = slug === 'architect';
  const bonusEach  = MILESTONE_BONUS_CREDITS[slug][35000];
  const bonusCount = getBonusCount(slug, entryMilestone, bonusCap);
  const maxCred    = getMaxCredits(slug, entryMilestone, bonusCap);
  const base       = BASE_CREDITS[slug];

  return (
    <div style={{ position: 'relative', ...(isPopular ? { transform: 'scale(1.06)', zIndex: 2 } : {}) }}>
      {isPopular && (
        <div style={s.popularBadge}>MOST POPULAR</div>
      )}
      <div
        style={{
          ...s.planCard,
          borderColor: isPopular ? cfg.color : isUser ? cfg.color : 'rgba(255,255,255,0.08)',
          boxShadow: isPopular
            ? `0 0 0 2px ${cfg.color}, 0 12px 40px ${cfg.color}44`
            : isUser ? `0 0 0 2px ${cfg.color}, 0 6px 28px rgba(0,0,0,0.35)` : '0 4px 20px rgba(0,0,0,0.3)',
          background: isPopular ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.05)',
        }}
      >
        {isUser && !isPopular && (
          <div style={{ ...s.yourPlanBadge, background: cfg.color }}>Your Plan</div>
        )}
        {isUser && isPopular && (
          <div style={{ ...s.yourPlanBadge, background: cfg.color }}>Your Plan</div>
        )}
        <div style={{ ...s.planColorBar, background: cfg.color }} />
        <div style={{ ...s.planCardInner, padding: innerPad || '24px 24px 28px' }}>
          <div style={s.planTopRow}>
            <h3 style={{ ...s.planName, color: cfg.color }}>{cfg.name}</h3>
            <div style={s.planPriceBlock}>
              <span style={{ ...s.planPrice, color: cfg.color }}>{cfg.price}</span>
              <span style={s.planPriceUsd}>USD</span>
              <span style={s.planPricePer}>/mo</span>
            </div>
          </div>
          <div style={s.planUrgency}>{PLAN_URGENCY[slug]}</div>
          <div style={s.planBaseCredits}>
            <span style={s.planCreditsNum}>{base}</span>
            <span style={s.planCreditsLabel}> credits/month to start</span>
          </div>
          <div style={s.planUsage}>{PLAN_USAGE[slug]}</div>
          <div style={s.planDivider} />
          <div style={s.planGrowthTitle}>Tier credit growth (current position)</div>
          <div style={s.planGrowthRow}>
            <span style={{ ...s.planGrowthPlus, color: cfg.color }}>+{bonusEach}</span>
            <span style={s.planGrowthDesc}>credits at each eligible milestone within tier</span>
          </div>
          <div style={s.planGrowthRow}>
            <span style={{ ...s.planGrowthPlus, color: cfg.color }}>{bonusCount}x</span>
            <span style={s.planGrowthDesc}>increases until {capLabel}</span>
          </div>
          <div style={s.planGrowthRow}>
            <span style={{ ...s.planGrowthPlus, color: cfg.color }}>{maxCred}</span>
            <span style={s.planGrowthDesc}>max credits/month reached</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── COMPONENT ──────────────────────────────────────────────────────────────

export default function GrowthPricing() {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    API.get('/v1/usage/current')
      .then(res => setUsage(res.data))
      .catch(() => null);
  }, []);

  const currentGen     = CURRENT_GENERATORS;
  const nextMilestone  = getNextMilestone(currentGen);
  const progressPct    = Math.min(100, Math.round((currentGen / nextMilestone.generators) * 100));
  const userPlanSlug   = usage?.plan_slug || null;
  const entryMilestone = getEntryMilestone(currentGen);
  const bonusCap       = getBonusCap(entryMilestone);
  const capLabel       = getCapLabel(bonusCap);

  const [calcImages,  setCalcImages]  = useState(1);
  const [calcQuality, setCalcQuality] = useState('draft');

  const formatGenerators = (n) => {
    if (n >= 1000) return `${(n / 1000).toFixed(0)}k`;
    return `${n}`;
  };

  return (
    <div style={s.page}>
      <ProductNav />
      <SideHelpPanel title="Growth &amp; Pricing">
        <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 20, overflowY: 'auto' }}>
          <div>
            <span style={hp.heading}>What This Page Is</span>
            <p style={hp.body}>This page shows how VISINVOKE pricing evolves with generator growth, and how your active subscription protects your rate over time.</p>
          </div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hp.heading}>Grandfathering</span>
            <p style={hp.body}>Grandfathering is tier-based. Your pricing position is set when you subscribe and applies within that plan’s tier limits.</p>
          </div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hp.heading}>Bonus Credits</span>
            <p style={hp.body}>At each milestone, active subscribers receive a bonus credit increase as a loyalty benefit. These are additive increments, not totals. Bonuses apply within your grandfathering band only.</p>
          </div>
        </div>
      </SideHelpPanel>

      <div style={s.container}>

        {/* ── SECTION 1 — HERO ── */}
        <div style={s.heroCard}>
          <div style={s.heroBadge}>UP TO 4,000+ CONTROLS PER GENERATOR</div>
          <h1 style={s.heroTitle}>VISINVOKE grows — your plan grows with it</h1>
          <p style={s.heroSub}>
            Grandfathering is tier-based. Your pricing position is set when you subscribe and applies within that plan’s tier limits.
            Up to 4,000+ total selectable controls per generator across a growing platform of D&amp;D and professional generators.
          </p>
          <div style={s.heroPillRow}>
            <div style={s.heroPill}><span style={s.pillIcon}>🔒</span> Tier-based grandfathering</div>
            <div style={s.heroPill}><span style={s.pillIcon}>📈</span> Credits grow with platform</div>
            <div style={s.heroPill}><span style={s.pillIcon}>⏳</span> Earlier entry secures stronger position</div>
          </div>
        </div>

        {/* ── SECTION 2 — WHY JOIN NOW ── */}
        <div style={s.whyCard}>
          <h2 style={s.whyTitle}>Why join now?</h2>
          <div style={s.whyGrid}>
            <div style={s.whyItem}>
              <div style={s.whyNum}>01</div>
              <div style={s.whyText}>
                Earlier subscribers secure stronger tier positions. The platform currently has a growing ecosystem of D&D and professional generators, each with up to 4,000+ selectable controls.
              </div>
            </div>
            <div style={s.whyItem}>
              <div style={s.whyNum}>02</div>
              <div style={s.whyText}>
                <strong>Credits can increase within your tier.</strong> At eligible expansion milestones, your included credits may increase automatically within your tier limits. No action needed.
              </div>
            </div>
            <div style={s.whyItem}>
              <div style={s.whyNum}>03</div>
              <div style={s.whyText}>
                <strong>Earlier subscribers can secure stronger positions.</strong> Users who subscribe now may enter at a stronger tier position than those who join after future milestones.
              </div>
            </div>
          </div>
        </div>

        {/* ── SECTION 3 — CURRENT STATUS ── */}
        <div style={s.statusCard}>
          <div style={s.statusRow}>
            <div>
              <div style={s.statusLabel}>VISINVOKE is currently at</div>
              <div style={s.statusValue}>{formatGenerators(currentGen)} generators</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={s.statusLabel}>Next milestone</div>
              <div style={s.statusValueRight}>{nextMilestone.label} generators</div>
            </div>
          </div>
          <div style={s.barTrack}>
            <div style={{ ...s.barFill, width: `${progressPct}%` }} />
          </div>
          <div style={s.statusNote}>
            Joining before {nextMilestone.label} locks your tier position and qualifies you for eligible credit increases within that tier.
          </div>
          <div style={s.statusFomo}>
            <span style={s.statusFomoDot} />
            Next milestone at {nextMilestone.label} generators
          </div>
        </div>

        {/* ── SECTION 4 — PLAN CARDS (3 + 2 layout) ── */}
        <div style={s.planGridTop}>
          {TOP_PLANS.map(slug => (
            <PlanCard
              key={slug}
              slug={slug}
              userPlanSlug={userPlanSlug}
              entryMilestone={entryMilestone}
              bonusCap={bonusCap}
              capLabel={capLabel}
              innerPad="24px 24px 28px"
            />
          ))}
        </div>
        <div style={s.planGridBottom}>
          {BOTTOM_PLANS.map(slug => (
            <PlanCard
              key={slug}
              slug={slug}
              userPlanSlug={userPlanSlug}
              entryMilestone={entryMilestone}
              bonusCap={bonusCap}
              capLabel={capLabel}
              innerPad="28px 28px 34px"
            />
          ))}
        </div>

        {/* ── SECTION 5 — MILESTONE TIMELINE ── */}
        <div style={s.card}>
          <h2 style={s.sectionTitle}>Generator milestones</h2>
          <p style={s.sectionSubtitle}>Each milestone may affect pricing for new subscribers. Active subscribers retain their tier position and may receive credit increases within their tier limits.</p>
          <div style={s.timeline}>
            {MILESTONES.map((m, i) => {
              const isCurrent = m.generators === currentGen;
              const isPast    = m.generators < currentGen;
              const isFuture  = m.generators > currentGen;
              const inBand    = m.generators > entryMilestone && m.generators <= bonusCap;
              return (
                <div key={m.generators} style={s.timelineItem}>
                  <div style={{
                    ...s.timelineDot,
                    background: isCurrent ? '#a855f7' : isPast ? '#4b5563' : inBand ? '#10b981' : '#374151',
                    border: isCurrent ? '3px solid #c084fc' : inBand ? '2px solid #10b981' : '2px solid transparent',
                    transform: isCurrent ? 'scale(1.3)' : 'scale(1)',
                  }} />
                  {i < MILESTONES.length - 1 && (
                    <div style={{
                      ...s.timelineLine,
                      top: 9,
                      background: isPast ? '#4b5563' : inBand ? '#10b98140' : '#1f2937',
                    }} />
                  )}
                  <div style={s.timelineLabelWrap}>
                    <div style={{
                      ...s.timelineLabel,
                      color: isCurrent ? '#c084fc' : isFuture && inBand ? '#10b981' : '#9ca3af',
                      fontWeight: isCurrent ? 800 : 600,
                    }}>
                      {m.label}
                    </div>
                    {isCurrent && <div style={s.timelineCurrent}>← NOW</div>}
                    {isFuture && inBand && <div style={s.timelineBonus}>+credits</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={s.timelineLegend}>
            <div style={s.legendItem}><div style={{ ...s.legendDot, background: '#a855f7' }} /> Current</div>
            <div style={s.legendItem}><div style={{ ...s.legendDot, background: '#10b981' }} /> Credit boost milestone</div>
            <div style={s.legendItem}><div style={{ ...s.legendDot, background: '#374151' }} /> Future milestone</div>
          </div>
        </div>

        {/* ── SECTION 6 — COMPARISON TABLE ── */}
        <div style={s.card}>
          <h2 style={s.sectionTitle}>Plan comparison</h2>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <colgroup>
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ ...s.th, textAlign: 'left' }}>Plan</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Current price (USD)</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Starting credits</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Max credits (joining now)</th>
                </tr>
              </thead>
              <tbody>
                {SUBSCRIPTION_PLANS.map(slug => {
                  const cfg     = PLAN_CONFIG[slug];
                  const isUser  = userPlanSlug === slug;
                  const base    = BASE_CREDITS[slug];
                  const maxCred = getMaxCredits(slug, entryMilestone, bonusCap);
                  return (
                    <tr key={slug} style={{ ...s.tr, background: isUser ? `${cfg.color}12` : 'transparent' }}>
                      <td style={{ ...s.td, color: cfg.color, fontWeight: 800, textAlign: 'left' }}>
                        {cfg.name}
                        {isUser && <span style={{ ...s.yourPlanInline, background: cfg.color }}>You</span>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{cfg.price} <span style={s.tdUsd}>USD</span><span style={s.tdSub}>/mo</span></td>
                      <td style={{ ...s.td, fontWeight: 800, textAlign: 'center' }}>{base}</td>
                      <td style={{ ...s.td, color: '#10b981', fontWeight: 800, textAlign: 'center' }}>{maxCred}</td>
                    </tr>
                  );
                })}
                <tr style={{ ...s.tr, borderTop: '2px solid rgba(255,255,255,0.1)' }}>
                  <td style={{ ...s.td, color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'left' }}>Best for</td>
                  {SUBSCRIPTION_PLANS.map(slug => (
                    <td key={slug} style={{ ...s.td, color: 'rgba(255,255,255,0.55)', fontSize: 13, textAlign: 'center' }}>{PLAN_BEST_FOR[slug]}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <div style={s.noLockIn}>No lock-in contracts. Cancel anytime.</div>
        </div>

        {/* ── SECTION 7 — GRANDFATHERING EXPLANATION ── */}
        <div style={s.gfCard}>
          <div style={s.gfTopLine}>Grandfathering is tier-based. Your pricing position is set when you subscribe and applies within that plan’s tier limits.</div>
          <h2 style={s.gfTitle}>How tier-based grandfathering works</h2>
          <div style={s.gfGrid}>
            <div style={s.gfItem}>
              <div style={s.gfIcon}>🔒</div>
              <div style={s.gfItemTitle}>Pricing follows your entry tier</div>
              <div style={s.gfItemText}>When you subscribe, you enter a pricing tier based on current platform milestones. This determines your long-term pricing position for that plan.</div>
            </div>
            <div style={s.gfItem}>
              <div style={s.gfIcon}>📈</div>
              <div style={s.gfItemTitle}>Credits increase within your tier</div>
              <div style={s.gfItemText}>As VISINVOKE expands, your included credits may increase automatically at eligible milestones within your tier.</div>
            </div>
            <div style={s.gfItem}>
              <div style={s.gfIcon}>🏁</div>
              <div style={s.gfItemTitle}>Each tier has a cap</div>
              <div style={s.gfItemText}>Credit increases apply only up to the maximum level defined for your grandfathering tier.</div>
            </div>
            <div style={s.gfItem}>
              <div style={s.gfIcon}>⬆️</div>
              <div style={s.gfItemTitle}>Upgrading resets your tier position</div>
              <div style={s.gfItemText}>If you upgrade to a higher plan, your position is recalculated based on the current milestone level of that plan.</div>
            </div>
          </div>
          <div style={s.gfBottomLine}>Earlier subscribers secure stronger tier positions. The platform currently has a growing ecosystem of D&D and professional generators, each with up to 4,000+ selectable controls.</div>
        </div>

        {/* ── SECTION 8 — QUALITY TIERS ── */}
        <div style={s.card}>
          <h2 style={s.sectionTitle}>Generation quality tiers</h2>
          <p style={s.sectionSubtitle}>Every generation uses credits. Higher quality uses more.</p>
          <div style={s.qualityGrid}>
            <div style={s.qualityCol}>
              <div style={s.qualityIcon}>⚡</div>
              <div style={s.qualityLabel}>Draft</div>
              <div style={s.qualityDesc}>Fast generation. Ideal for exploring ideas and testing prompts.</div>
              <div style={s.qualityCost}>1× credit</div>
            </div>
            <div style={{ ...s.qualityCol, borderColor: '#8b5cf640' }}>
              <div style={s.qualityIcon}>✨</div>
              <div style={s.qualityLabel}>Refined</div>
              <div style={s.qualityDesc}>Improved output quality. Best for shortlisting and client previews.</div>
              <div style={{ ...s.qualityCost, color: '#8b5cf6' }}>2× credits</div>
            </div>
            <div style={{ ...s.qualityCol, borderColor: '#f9731640' }}>
              <div style={s.qualityIcon}>🏆</div>
              <div style={s.qualityLabel}>Final</div>
              <div style={s.qualityDesc}>Highest quality output. For finished work ready to use or share.</div>
              <div style={{ ...s.qualityCost, color: '#f97316' }}>4× credits</div>
            </div>
          </div>

          {/* ── CREDIT CALCULATOR ── */}
          <div style={s.calcWrap}>
            <div style={s.calcTitle}>How many credits will this use?</div>

            <div style={s.calcRow}>
              <div style={s.calcLabel}>Images</div>
              <div style={s.calcBtnGroup}>
                {[1, 2, 4].map(n => (
                  <button
                    key={n}
                    type="button"
                    style={{ ...s.calcBtn, ...(calcImages === n ? s.calcBtnActive : {}) }}
                    onClick={() => setCalcImages(n)}
                  >{n}</button>
                ))}
              </div>
            </div>

            <div style={s.calcRow}>
              <div style={s.calcLabel}>Quality</div>
              <div style={s.calcBtnGroup}>
                {[
                  { key: 'draft',   label: 'Draft',   mult: 1 },
                  { key: 'refined', label: 'Refined', mult: 2 },
                  { key: 'final',   label: 'Final',   mult: 4 },
                ].map(q => (
                  <button
                    key={q.key}
                    type="button"
                    style={{ ...s.calcBtn, ...(calcQuality === q.key ? s.calcBtnActive : {}) }}
                    onClick={() => setCalcQuality(q.key)}
                  >{q.label}</button>
                ))}
              </div>
            </div>

            <div style={s.calcGenerating}>
              You are generating: <strong>{calcImages} image{calcImages > 1 ? 's' : ''}</strong> at <strong>{{ draft: 'Draft', refined: 'Refined', final: 'Final' }[calcQuality]}</strong> quality
            </div>
            <div style={s.calcTotal}>
              Total cost: <span style={s.calcTotalNum}>{calcImages * { draft: 1, refined: 2, final: 4 }[calcQuality]}</span>
              <span style={s.calcTotalLabel}> credit{calcImages * { draft: 1, refined: 2, final: 4 }[calcQuality] !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {/* ── REFERENCE TABLE ── */}
          <div style={s.costTableWrap}>
            <div style={s.costTableTitle}>Credit reference</div>
            <div style={s.costTableGrid}>
              {[
                { images: '1 image',  draft: '1 credit',  refined: '2 credits', final: '4 credits'  },
                { images: '2 images', draft: '2 credits', refined: '4 credits', final: '8 credits'  },
                { images: '4 images', draft: '4 credits', refined: '8 credits', final: '16 credits' },
              ].map(row => (
                <div key={row.images} style={s.costRow}>
                  <span style={s.costRowImages}>{row.images}</span>
                  <span style={s.costRowDraft}><span style={s.costRowQuality}>Draft:</span> {row.draft}</span>
                  <span style={s.costRowRefined}><span style={s.costRowQuality}>Refined:</span> {row.refined}</span>
                  <span style={s.costRowFinal}><span style={s.costRowQuality}>Final:</span> {row.final}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── STYLES ─────────────────────────────────────────────────────────────────

const s = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a1840 0%, #2d3a4a 50%, #701030 100%)',
    fontFamily: "'Segoe UI', sans-serif",
    paddingBottom: 60,
  },
  container: {
    maxWidth: 1560,
    margin: '0 auto',
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
  },

  // Hero
  heroCard: {
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
    borderRadius: 20,
    padding: '56px 40px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
    border: '1px solid rgba(139,92,246,0.3)',
    textAlign: 'center',
  },
  heroBadge: {
    display: 'inline-block',
    background: 'rgba(139,92,246,0.2)',
    border: '1px solid rgba(139,92,246,0.5)',
    color: '#c084fc',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    padding: '5px 14px',
    borderRadius: 20,
    marginBottom: 22,
    textTransform: 'uppercase',
  },
  heroTitle: {
    color: '#ffffff',
    fontSize: 36,
    fontWeight: 800,
    margin: '0 0 18px',
    lineHeight: 1.25,
  },
  heroSub: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: 17,
    lineHeight: 1.85,
    margin: '0 auto 32px',
    maxWidth: 620,
  },
  heroPillRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  heroPill: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: 'rgba(255,255,255,0.85)',
    fontSize: 13,
    fontWeight: 600,
    padding: '9px 20px',
    borderRadius: 30,
    display: 'flex',
    alignItems: 'center',
    gap: 7,
  },
  pillIcon: {
    fontSize: 15,
  },

  // Why Join
  whyCard: {
    background: 'rgba(16,185,129,0.08)',
    border: '1px solid rgba(16,185,129,0.22)',
    borderRadius: 16,
    padding: '36px 40px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
  },
  whyTitle: {
    color: '#ffffff',
    fontSize: 26,
    fontWeight: 800,
    margin: '0 0 28px',
  },
  whyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 24,
  },
  whyItem: {
    display: 'flex',
    gap: 18,
    alignItems: 'flex-start',
  },
  whyNum: {
    fontSize: 26,
    fontWeight: 900,
    color: '#10b981',
    opacity: 0.45,
    lineHeight: 1,
    flexShrink: 0,
    marginTop: 2,
  },
  whyText: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.85,
  },

  // Status
  statusCard: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: '28px 32px',
  },
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 18,
    flexWrap: 'wrap',
    gap: 8,
  },
  statusLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: 5,
  },
  statusValue: {
    fontSize: 22,
    fontWeight: 800,
    color: '#ffffff',
  },
  statusValueRight: {
    fontSize: 22,
    fontWeight: 800,
    color: '#a855f7',
    textAlign: 'right',
  },
  barTrack: {
    height: 10,
    background: 'rgba(255,255,255,0.08)',
    borderRadius: 5,
    overflow: 'hidden',
    marginBottom: 16,
  },
  barFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #7c3aed, #a855f7)',
    borderRadius: 5,
    transition: 'width 0.5s ease',
  },
  statusNote: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.5)',
    lineHeight: 1.7,
  },

  popularBadge: {
    position: 'absolute',
    top: -14,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#8b5cf6',
    color: '#fff',
    fontSize: 10,
    fontWeight: 800,
    padding: '4px 14px',
    borderRadius: 20,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    zIndex: 3,
    boxShadow: '0 2px 10px rgba(139,92,246,0.5)',
  },
  planUrgency: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
    marginBottom: 14,
    fontStyle: 'italic',
  },
  planUsage: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.4)',
    marginTop: 4,
    marginBottom: 16,
    fontFamily: "'Segoe UI', sans-serif",
  },
  statusFomo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
    fontSize: 14,
    fontWeight: 700,
    color: '#F59E0B',
  },
  statusFomoDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#F59E0B',
    flexShrink: 0,
    boxShadow: '0 0 6px #F59E0B',
  },
  gfTopLine: {
    fontSize: 16,
    fontWeight: 700,
    color: '#10b981',
    marginBottom: 16,
    padding: '10px 16px',
    background: 'rgba(16,185,129,0.08)',
    border: '1px solid rgba(16,185,129,0.2)',
    borderRadius: 8,
  },
  gfBottomLine: {
    fontSize: 14,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.45)',
    marginTop: 20,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  noLockIn: {
    marginTop: 14,
    textAlign: 'center',
    fontSize: 13,
    color: 'rgba(255,255,255,0.35)',
    fontStyle: 'italic',
  },
  calcGenerating: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.55)',
    textAlign: 'center',
    marginBottom: 4,
  },

  // Plan cards — 3+2 layout
  planGridTop: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 20,
    alignItems: 'start',
    paddingTop: 20,
    paddingBottom: 8,
  },
  planGridBottom: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 20,
    marginTop: -12,
  },
  planCard: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  planColorBar: {
    height: 4,
    width: '100%',
  },
  planCardInner: {
    padding: '28px 28px 34px',
  },
  planTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  planName: {
    fontSize: 22,
    fontWeight: 800,
    margin: 0,
  },
  planPriceBlock: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 2,
  },
  planPrice: {
    fontSize: 24,
    fontWeight: 800,
  },
  planPriceUsd: { fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.05em', alignSelf: 'flex-end', marginBottom: 6, marginLeft: 2 },
  planPricePer: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.35)',
    fontWeight: 500,
  },
  planBaseCredits: {
    marginBottom: 20,
  },
  planCreditsNum: {
    fontSize: 38,
    fontWeight: 900,
    color: '#ffffff',
  },
  planCreditsLabel: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.45)',
  },
  planDivider: {
    height: 1,
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 18,
  },
  planGrowthTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.35)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: 13,
  },
  planGrowthRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 10,
  },
  planGrowthPlus: {
    fontSize: 17,
    fontWeight: 800,
    minWidth: 40,
  },
  planGrowthDesc: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.55)',
    lineHeight: 1.5,
  },
  yourPlanBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 20,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    zIndex: 1,
  },

  // Shared card
  card: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    padding: '32px 36px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 800,
    color: '#ffffff',
    margin: '0 0 8px',
  },
  sectionSubtitle: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.45)',
    margin: '0 0 28px',
    lineHeight: 1.75,
  },

  // Timeline
  timeline: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    overflowX: 'auto',
    paddingBottom: 16,
    paddingTop: 16,
    minHeight: 100,
  },
  timelineItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    minWidth: 80,
    position: 'relative',
    gap: 10,
  },
  timelineDot: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    flexShrink: 0,
    zIndex: 1,
    transition: 'transform 0.2s',
  },
  timelineLine: {
    position: 'absolute',
    top: 9,
    left: '50%',
    width: '100%',
    height: 2,
    zIndex: 0,
  },
  timelineLabelWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  timelineLabel: {
    fontSize: 15,
    textAlign: 'center',
  },
  timelineCurrent: {
    fontSize: 10,
    color: '#c084fc',
    fontWeight: 700,
    letterSpacing: '0.05em',
  },
  timelineBonus: {
    fontSize: 10,
    color: '#10b981',
    fontWeight: 700,
  },
  timelineLegend: {
    display: 'flex',
    gap: 20,
    marginTop: 20,
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
  },

  // Table
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 17,
  },
  th: {
    textAlign: 'left',
    padding: '13px 22px',
    color: 'rgba(255,255,255,0.4)',
    fontWeight: 700,
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  td: {
    padding: '18px 22px',
    color: 'rgba(255,255,255,0.85)',
    fontWeight: 500,
    verticalAlign: 'middle',
  },
  tdUsd: { fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.05em', marginRight: 2 },
  tdSub: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.3)',
    fontWeight: 400,
  },
  yourPlanInline: {
    display: 'inline-block',
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginLeft: 8,
    verticalAlign: 'middle',
  },

  // Grandfathering — fixed 2×2
  gfCard: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: '36px 40px',
    boxShadow: '0 6px 28px rgba(0,0,0,0.28)',
  },
  gfTitle: {
    fontSize: 28,
    fontWeight: 800,
    color: '#ffffff',
    margin: '0 0 28px',
  },
  gfGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 24,
  },
  gfItem: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    padding: '30px 32px',
  },
  gfIcon: {
    fontSize: 32,
    marginBottom: 14,
  },
  gfItemTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: 10,
  },
  gfItemText: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.58)',
    lineHeight: 1.8,
  },

  // Quality
  qualityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    marginBottom: 28,
  },
  qualityCol: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 14,
    padding: '24px 20px',
    textAlign: 'center',
  },
  qualityIcon: {
    fontSize: 26,
    marginBottom: 10,
  },
  qualityLabel: {
    fontWeight: 800,
    fontSize: 16,
    color: '#ffffff',
    marginBottom: 8,
  },
  qualityDesc: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.5)',
    lineHeight: 1.7,
    marginBottom: 14,
  },
  qualityCost: {
    fontSize: 15,
    fontWeight: 700,
    color: '#10b981',
  },

  // Credit cost table
  costTableWrap: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 14,
    padding: '20px 24px',
    opacity: 0.65,
  },
  costTableTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.5)',
    marginBottom: 14,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  costTableGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  costRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    alignItems: 'center',
    padding: '10px 8px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    gap: 12,
  },
  costRowImages: {
    fontSize: 14,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.7)',
  },
  costRowDraft: {
    fontSize: 14,
    color: '#10b981',
    fontWeight: 600,
  },
  costRowRefined: {
    fontSize: 14,
    color: '#8b5cf6',
    fontWeight: 600,
  },
  costRowFinal: {
    fontSize: 14,
    color: '#f97316',
    fontWeight: 600,
  },
  costRowQuality: {
    color: 'rgba(255,255,255,0.3)',
    fontWeight: 400,
    marginRight: 4,
  },

  // Credit calculator
  calcWrap: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 16,
    padding: '36px 40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 28,
    marginBottom: 16,
  },
  calcTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: '#ffffff',
    textAlign: 'center',
  },
  calcRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  calcLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    minWidth: 60,
    textAlign: 'right',
  },
  calcBtnGroup: {
    display: 'flex',
    gap: 10,
  },
  calcBtn: {
    ...glassSurface('#334155', '#64748B', '#94A3B8'),
    padding: '10px 28px',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Segoe UI', sans-serif",
    transition: 'all 0.15s',
  },
  calcBtnActive: {
    ...glassSurface('#A66D05', '#F0B429', '#FBBF24'),
  },
  calcTotal: {
    fontSize: 22,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.6)',
    textAlign: 'center',
    marginTop: 8,
  },
  calcTotalNum: {
    fontSize: 56,
    fontWeight: 900,
    color: '#F0B429',
    lineHeight: 1,
    display: 'inline-block',
    marginLeft: 8,
  },
  calcTotalLabel: {
    fontSize: 22,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.6)',
  },
};

const hp = {
  heading: { fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 0, marginBottom: 8, display: 'block' },
  body:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, margin: '0 0 10px 0' },
  divider: { height: 1, background: 'rgba(255,255,255,0.05)', margin: '12px 0' },
};
